import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import DraggableScroll from '../../../../draggableScroll';
import HorizontalAnswer from '../horizontalAnswer';
import ScrollIndicators from '../../../../scrollIndicators';
import {
  isAttributeQuestionAnswerSelectedCheck,
  updateScrollPosition,
} from '../../../../../../utility/fillSurveyUtils';
import { prepareAnswerWeight } from '../../../../../../utility/attribute';
import {
  usePreviousValue,
  useDraggableScrollPosition,
} from '../../../../../../utility/hooks';
import { NEUTRAL_ANSWER_WEIGHT } from '../../../../questionEntry/config';

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  root: {
    display: 'flex',
    position: 'relative',
    maxWidth: '100%',
  },
  rootAttributePreview: {
    // subtract weight label
    maxWidth: 'calc(100% - 40px)',
  },
  answersScroll: {
    display: 'flex',
    width: '100%',
    paddingTop: spacing(10),
  },
  groupReviewAnswersScroll: {
    padding: spacing(0, 0, 2, 2),
    [breakpoints.up('sm')]: {
      padding: spacing(0, 0, 4, 4),
    },
  },
  answerListWrapper: {
    width: 'auto',
  },
  answersWrapper: {
    display: 'flex',
  },
  answerWrapper: {
    marginRight: spacing(1),
    display: 'grid',
    justifyContent: 'flex-start',
    '&:last-child': {
      marginRight: 0,
    },
    [breakpoints.up('sm')]: {
      marginRight: spacing(2),
    },
  },
  answerWrapperGroup: {
    marginRight: spacing(1),
    '&:last-child': {
      marginRight: spacing(2),
    },
    [breakpoints.up('sm')]: {
      marginRight: spacing(2),
      '&:last-child': {
        marginRight: spacing(4),
      },
    },
  },
  answerWeight: {
    justifyContent: 'center',
    display: 'flex',
    fontSize: 12,
    fontFamily: 'ProximaNova-Regular',
    marginTop: spacing(3),
  },
  arrow: {
    width: 24,
    height: 24,
    [breakpoints.up('xLg')]: {
      width: 24,
      height: 24,
    },
  },
  arrowLeft: {
    left: 8,
    top: 8,
    [breakpoints.up('sm')]: {
      left: 16,
    },
  },
  arrowLeftSingle: {
    left: 0,
  },
  arrowRight: {
    right: 8,
    top: 8,
    [breakpoints.up('sm')]: {
      right: 16,
    },
  },
  arrowRightSingle: {
    right: 0,
  },
  arrowRightAttributePreview: {
    top: 8,
  },
  arrowLeftAttributePreview: {
    top: 8,
  },
}));

const HorizontalAnswersGroup = ({
  neutralAnswerLabel,
  answers,
  subject = {},
  selection = {},
  questionId = 0,
  isAttributePreview = false,
  isSingleReview = false,
  isDisabled = false,
  hasAddCommentButton = false,
  onSelect = () => {},
}) => {
  const classes = useStyles();
  const draggableScrollRef = useRef(null);
  const answersRef = useRef(null);
  const numberOfAnswers = answers.length;
  const previousQuestionId = usePreviousValue(questionId);

  const [
    activeScroll,
    onChangeScrollPosition,
    onScrollToRight,
    ,
    onScrollToStart,
  ] = useDraggableScrollPosition(draggableScrollRef, answersRef);

  const handleSelect = (answerId, user) => () => {
    if (!isDisabled) {
      onSelect(questionId, answerId, user, hasAddCommentButton);
    }
  };

  const handleScrollEnd = () => onChangeScrollPosition();

  useEffect(() => {
    if (previousQuestionId !== questionId) {
      onChangeScrollPosition();
      updateScrollPosition(draggableScrollRef.current, false);
    }
  }, [previousQuestionId, questionId, onChangeScrollPosition]);

  return (
    <div
      className={classNames(classes.root, {
        [classes.rootAttributePreview]: isAttributePreview,
        [classes.groupReviewAnswersScroll]: !isSingleReview,
      })}
    >
      <DraggableScroll
        ref={draggableScrollRef}
        className={classes.answersScroll}
        onEndScroll={handleScrollEnd}
      >
        <div ref={answersRef} className={classes.answerListWrapper}>
          <div className={classes.answersWrapper}>
            {answers.map((answer, index) => {
              const isSelected = isAttributeQuestionAnswerSelectedCheck(
                selection,
                answer.id,
                subject
              );
              const answerWeight = prepareAnswerWeight(answer.weight);

              return (
                <div
                  key={`answer_item_${answer.id ?? answer.order ?? index}`}
                  className={classNames(classes.answerWrapper, {
                    [classes.answerWrapperGroup]: !isSingleReview,
                  })}
                >
                  <HorizontalAnswer
                    neutralAnswerLabel={neutralAnswerLabel}
                    numberOfAnswers={numberOfAnswers}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    answer={answer}
                    onSelect={handleSelect(answer.id, subject)}
                  />
                  {isAttributePreview && (
                    <div className={classes.answerWeight}>
                      {answerWeight !== NEUTRAL_ANSWER_WEIGHT
                        ? answerWeight
                        : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </DraggableScroll>
      <ScrollIndicators
        arrowClass={classes.arrow}
        arrowLeftClass={classNames(classes.arrowLeft, {
          [classes.arrowLeftSingle]: isSingleReview,
          [classes.arrowLeftAttributePreview]: isAttributePreview,
        })}
        arrowRightClass={classNames(classes.arrowRight, {
          [classes.arrowRightSingle]: isSingleReview,
          [classes.arrowRightAttributePreview]: isAttributePreview,
        })}
        activeScroll={activeScroll}
        onScrollToRight={onScrollToRight}
        onScrollToStart={onScrollToStart}
      />
    </div>
  );
};

HorizontalAnswersGroup.propTypes = {
  answers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  questionId: PropTypes.number,
  subject: PropTypes.shape({}),
  isSingleReview: PropTypes.bool,
  isDisabled: PropTypes.bool,
  neutralAnswerLabel: PropTypes.string.isRequired,
  selection: PropTypes.shape({}),
  onSelect: PropTypes.func,
  isAttributePreview: PropTypes.bool,
  hasAddCommentButton: PropTypes.bool,
};

export default HorizontalAnswersGroup;
